/**
 * Brand icons for the password vault. SVG paths trace official platform marks
 * with neutral colors so they look great on dark mode.
 *
 * Used in VaultPage and the platform picker.
 */

export const PLATFORMS = [
  { id: "instagram", label: "Instagram", color: "#E1306C" },
  { id: "facebook",  label: "Facebook",  color: "#1877F2" },
  { id: "tiktok",    label: "TikTok",    color: "#25F4EE" },
  { id: "youtube",   label: "YouTube",   color: "#FF0033" },
  { id: "google",    label: "Google",    color: "#4285F4" },
  { id: "twitter",   label: "X / Twitter", color: "#E7E9EA" },
  { id: "linkedin",  label: "LinkedIn",  color: "#0A66C2" },
  { id: "whatsapp",  label: "WhatsApp",  color: "#25D366" },
  { id: "spotify",   label: "Spotify",   color: "#1DB954" },
  { id: "other",     label: "Other",     color: "#9CA3AF" },
];

export const PLATFORM_MAP = Object.fromEntries(PLATFORMS.map((p) => [p.id, p]));

/* eslint-disable max-len */
function Svg({ children, size = 20 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
      {children}
    </svg>
  );
}

export function PlatformIcon({ platform, size = 20, monochrome = false }) {
  const c = monochrome ? "currentColor" : (PLATFORM_MAP[platform]?.color || "#9CA3AF");
  switch (platform) {
    case "instagram":
      return (
        <Svg size={size}>
          <path fill={c} d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23a3.8 3.8 0 01-.9 1.38c-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.8 3.8 0 01-1.38-.9 3.8 3.8 0 01-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zm0 1.95c-3.14 0-3.51.01-4.75.07-1.07.05-1.65.23-2.04.38-.51.2-.88.44-1.27.83-.39.39-.63.76-.83 1.27-.15.39-.33.97-.38 2.04-.06 1.24-.07 1.61-.07 4.75s.01 3.51.07 4.75c.05 1.07.23 1.65.38 2.04.2.51.44.88.83 1.27.39.39.76.63 1.27.83.39.15.97.33 2.04.38 1.24.06 1.61.07 4.75.07s3.51-.01 4.75-.07c1.07-.05 1.65-.23 2.04-.38.51-.2.88-.44 1.27-.83.39-.39.63-.76.83-1.27.15-.39.33-.97.38-2.04.06-1.24.07-1.61.07-4.75s-.01-3.51-.07-4.75c-.05-1.07-.23-1.65-.38-2.04a3.42 3.42 0 00-.83-1.27 3.42 3.42 0 00-1.27-.83c-.39-.15-.97-.33-2.04-.38-1.24-.06-1.61-.07-4.75-.07zm0 3.32a4.57 4.57 0 110 9.14 4.57 4.57 0 010-9.14zm0 1.95a2.62 2.62 0 100 5.24 2.62 2.62 0 000-5.24zm5.81-2.17a1.07 1.07 0 11-2.14 0 1.07 1.07 0 012.14 0z" />
        </Svg>
      );
    case "facebook":
      return (
        <Svg size={size}>
          <path fill={c} d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.19 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.49-3.91 3.78-3.91 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.58v1.88h2.78l-.44 2.91h-2.33V22c4.78-.75 8.44-4.92 8.44-9.94z" />
        </Svg>
      );
    case "tiktok":
      return (
        <Svg size={size}>
          <path fill={c} d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43V8.65a8.16 8.16 0 004.77 1.52V6.69h-1.84z" />
        </Svg>
      );
    case "youtube":
      return (
        <Svg size={size}>
          <path fill={c} d="M23.5 6.2a3 3 0 00-2.1-2.13C19.4 3.5 12 3.5 12 3.5s-7.4 0-9.4.57A3 3 0 00.5 6.2 31.6 31.6 0 000 12a31.6 31.6 0 00.5 5.8 3 3 0 002.1 2.13C4.6 20.5 12 20.5 12 20.5s7.4 0 9.4-.57a3 3 0 002.1-2.13A31.6 31.6 0 0024 12a31.6 31.6 0 00-.5-5.8zM9.75 15.5v-7L15.5 12l-5.75 3.5z" />
        </Svg>
      );
    case "google":
      // Multi-color G mark (uses brand colors regardless of monochrome flag to keep recognizable)
      return (
        <Svg size={size}>
          <path fill={monochrome ? c : "#4285F4"} d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z" />
          <path fill={monochrome ? c : "#34A853"} d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0012 23z" />
          <path fill={monochrome ? c : "#FBBC05"} d="M5.84 14.12a6.6 6.6 0 010-4.24V7.04H2.18a11 11 0 000 9.92l3.66-2.84z" />
          <path fill={monochrome ? c : "#EA4335"} d="M12 5.38c1.62 0 3.06.56 4.21 1.65l3.15-3.15C17.46 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
        </Svg>
      );
    case "twitter":
      return (
        <Svg size={size}>
          <path fill={c} d="M18.244 2.25h3.308l-7.227 8.26L22.75 21.75h-6.78l-5.16-6.74-5.91 6.74H1.59L9.42 12.84 1.25 2.25h6.96l4.67 6.16 5.36-6.16zm-1.16 17.52h1.84L7.084 4.13H5.117L17.084 19.77z" />
        </Svg>
      );
    case "linkedin":
      return (
        <Svg size={size}>
          <path fill={c} d="M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2zM8.34 18.34H5.67V9.67h2.67v8.67zm-1.34-9.86a1.55 1.55 0 110-3.1 1.55 1.55 0 010 3.1zm11.34 9.86h-2.67v-4.21c0-1 0-2.3-1.4-2.3s-1.62 1.09-1.62 2.22v4.29h-2.67V9.67h2.56v1.19h.04a2.81 2.81 0 012.53-1.39c2.71 0 3.21 1.78 3.21 4.1v4.77z" />
        </Svg>
      );
    case "whatsapp":
      return (
        <Svg size={size}>
          <path fill={c} d="M17.47 14.38c-.3-.15-1.77-.87-2.05-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.27-.47-2.41-1.49-.89-.79-1.5-1.77-1.67-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.21-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.49 0 1.47 1.07 2.89 1.22 3.09.15.2 2.11 3.22 5.11 4.51.71.31 1.27.49 1.7.62.71.23 1.36.2 1.88.12.57-.09 1.77-.72 2.02-1.41.25-.7.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35zM12 2a10 10 0 00-8.6 15.05L2 22l5.07-1.33A10 10 0 1012 2z" />
        </Svg>
      );
    case "spotify":
      return (
        <Svg size={size}>
          <path fill={c} d="M12 2a10 10 0 100 20 10 10 0 000-20zm4.62 14.43a.62.62 0 01-.86.2c-2.34-1.43-5.29-1.75-8.76-.96a.62.62 0 11-.28-1.22c3.8-.87 7.06-.5 9.69 1.11.3.18.39.57.21.87zm1.23-2.74a.78.78 0 01-1.07.26c-2.68-1.65-6.77-2.13-9.94-1.17a.78.78 0 11-.45-1.49c3.64-1.1 8.15-.57 11.22 1.31.36.22.48.7.24 1.09zm.11-2.85c-3.21-1.91-8.51-2.08-11.58-1.15a.93.93 0 11-.54-1.78c3.52-1.07 9.37-.87 13.07 1.33a.93.93 0 11-.95 1.6z" />
        </Svg>
      );
    case "other":
    default:
      return (
        <Svg size={size}>
          <path fill={c} d="M12 1l3.09 6.26L22 8.27l-5 4.87 1.18 6.88L12 16.77l-6.18 3.25L7 13.14 2 8.27l6.91-1.01L12 1z" />
        </Svg>
      );
  }
}
